import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api/client";

export type DraftSaveStatus = "idle" | "saving" | "saved";

const DEBOUNCE_MS = 800;

/** Same cache entry `useIntakeDraft` reads, so a remount restores the latest answers. */
export function intakeDraftKey(formSlug: string) {
  return ["me", "intake", "draft", formSlug] as const;
}

type DraftResponse = {
  form_slug: string;
  answers: Record<string, unknown>;
  updated_at: string | null;
};

/**
 * Debounced autosave for one intake draft. `PUT /me/intake/draft/{slug}`
 * REPLACES the stored answers, so every save sends the whole answer set.
 *
 * - Once the stored draft has loaded (`baseLoaded`), the local answers
 *   already include it and are sent as they are. Before that (still loading,
 *   or the read failed), a save first reads the stored draft and keeps
 *   anything the patient hasn't seen under their edits, so an early edit is
 *   never dropped and never wipes seeded answers.
 * - Ordinary saves run one at a time, in order. When the tab is hidden, the
 *   page unloads or the form unmounts (route change), the latest answers are
 *   sent at once with `keepalive` and any older save still in flight is
 *   aborted, so the newest set is the one that lands.
 * - Each save is mirrored into the query cache, so a patient who taps away
 *   to Learn and comes back sees their answers before the network catches up.
 * - A failed save is silent; the next change sends everything again.
 */
export function useHealthieDraftAutosave(
  formSlug: string,
  { enabled, baseLoaded }: { enabled: boolean; baseLoaded: boolean },
) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<DraftSaveStatus>("idle");
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const baseLoadedRef = useRef(baseLoaded);
  baseLoadedRef.current = baseLoaded;
  // Answers waiting on the debounce, then answers flushed but not yet sent.
  const pending = useRef<Record<string, unknown> | null>(null);
  const unsent = useRef<Record<string, unknown> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const inFlight = useRef<AbortController | null>(null);
  const stopped = useRef(false);
  const path = `/me/intake/draft/${formSlug}`;

  const cancelTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  const flush = useCallback(
    (immediate = false) => {
      cancelTimer();
      if (stopped.current) return queue.current;
      if (pending.current) {
        unsent.current = pending.current;
        pending.current = null;
      }
      const answers = unsent.current;
      if (!answers) return queue.current;

      const prime = (stored: Record<string, unknown>) =>
        queryClient.setQueryData(intakeDraftKey(formSlug), {
          form_slug: formSlug,
          answers: stored,
          updated_at: new Date().toISOString(),
        });
      const save = async (signal: AbortSignal) => {
        let stored = answers;
        if (baseLoadedRef.current) {
          prime(stored);
        } else {
          const current = await apiFetch<DraftResponse>(path, { signal });
          stored = { ...(current.answers ?? {}), ...answers };
          prime(stored);
        }
        await apiFetch(path, {
          method: "PUT",
          body: JSON.stringify({ answers: stored }),
          keepalive: immediate,
          signal,
        });
      };
      // A save that hasn't started yet is skipped once newer answers (or a
      // pause) supersede it; the newer save carries the full set anyway.
      const start = () => {
        if (stopped.current || unsent.current !== answers)
          return Promise.resolve();
        unsent.current = null;
        const controller = new AbortController();
        inFlight.current = controller;
        return save(controller.signal).then(
          () => {
            if (!pending.current && !unsent.current) setStatus("saved");
          },
          () => setStatus("idle"),
        );
      };

      setStatus("saving");
      if (immediate) {
        inFlight.current?.abort();
        queue.current = start();
      } else {
        queue.current = queue.current.then(start);
      }
      return queue.current;
    },
    [formSlug, path, queryClient],
  );

  const schedule = useCallback(
    (answers: Record<string, unknown>) => {
      if (!enabledRef.current || stopped.current) return;
      pending.current = answers;
      cancelTimer();
      timer.current = setTimeout(() => void flush(), DEBOUNCE_MS);
    },
    [flush],
  );

  /** Hold saves while submitting; resolves once in-flight saves have landed. */
  const pause = useCallback(() => {
    cancelTimer();
    pending.current = null;
    unsent.current = null;
    stopped.current = true;
    return queue.current;
  }, []);

  const resume = useCallback(() => {
    stopped.current = false;
  }, []);

  /** After a successful submit the backend deletes the draft; keep it deleted. */
  const clear = useCallback(() => {
    void pause();
    queryClient.setQueryData(intakeDraftKey(formSlug), {
      form_slug: formSlug,
      answers: {},
      updated_at: null,
    });
  }, [formSlug, pause, queryClient]);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void flush(true);
    };
    const onPageHide = () => void flush(true);
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", onPageHide);
      // Route change: unmounting must not drop the last few keystrokes.
      void flush(true);
    };
  }, [flush]);

  return { status, schedule, pause, resume, clear };
}
